import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BookMarked, Loader2, Trophy, Star, ScrollText, Search, Lock, Library } from "lucide-react";
import { LIVELLO, RARITA_STYLE } from "@/lib/labels";

function Stat({ label, value, sub, icon: Icon }) {
  return (
    <div className="bg-card border border-border rounded-md p-5">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="h-4 w-4 text-accent" strokeWidth={1.5} />}
        <p className="font-serif text-3xl font-bold text-primary">{value}</p>
      </div>
      <p className="text-sm font-medium">{label}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

export default function Libretto() {
  const [data, setData] = useState(null);
  const [codex, setCodex] = useState(null);
  const [q, setQ] = useState("");
  const [filtro, setFiltro] = useState("tutti");

  useEffect(() => {
    api.get("/libretto").then((res) => setData(res.data));
    api.get("/codex").then((res) => setCodex(res.data)).catch(() => setCodex({ voci: [], totale: 0, sbloccate: 0, corsi: [] }));
  }, []);

  const vociFiltrate = useMemo(() => {
    if (!codex) return [];
    const term = q.trim().toLowerCase();
    return codex.voci.filter((v) => {
      if (filtro !== "tutti" && v.corso_id !== filtro) return false;
      if (term && !v.termine.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [codex, q, filtro]);

  if (!data)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="h-8 w-8 animate-spin text-primary" strokeWidth={1.5} />
        </div>
      </div>
    );

  const maestriaPct = Math.round((data.maestria / data.maestria_max) * 100);
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "";

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center gap-3 mb-2">
          <BookMarked className="h-6 w-6 text-primary" strokeWidth={1.5} />
          <h1 className="font-serif text-4xl font-bold">Registro della Bottega</h1>
        </div>
        <p className="text-muted-foreground mb-8">Le carte delle Prove superate e il glossario dei concetti che sblocchi in Bottega.</p>

        {/* Maestria hero */}
        <div className="bg-card border border-border rounded-md p-6 mb-6" data-testid="maestria-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Trophy className="h-6 w-6 text-accent" strokeWidth={1.5} />
              <span className="text-sm font-medium">Maestria</span>
              {data.grado && <Badge className="rounded-full bg-primary text-primary-foreground ml-1" data-testid="grado-maestria">{data.grado}</Badge>}
            </div>
            <p className="font-serif text-3xl font-bold text-primary tabular-nums">
              {data.maestria.toLocaleString("it-IT")}<span className="text-muted-foreground text-base">/{data.maestria_max.toLocaleString("it-IT")}</span>
            </p>
          </div>
          <Progress value={maestriaPct} className="h-2.5" />
          <p className="text-sm text-muted-foreground mt-2">La tua Maestria cresce a ogni Prova superata.</p>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-8">
          <Stat label="Stelle acquisite" value={`${data.cfu_acquisiti}/${data.cfu_totali}`} icon={Star} />
          <Stat label="Prove superate" value={data.n_esami} sub="carte nel Codex" icon={ScrollText} />
        </div>

        <Tabs defaultValue="carte" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="carte" data-testid="tab-carte">
              <ScrollText className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Carte ({data.n_esami})
            </TabsTrigger>
            <TabsTrigger value="glossario" data-testid="tab-glossario">
              <Library className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Glossario{codex ? ` (${codex.sbloccate}/${codex.totale})` : ""}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="carte">
            {data.esami.length === 0 ? (
              <div className="bg-card border border-border rounded-md p-10 text-center">
                <ScrollText className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" strokeWidth={1.5} />
                <p className="text-muted-foreground">Il tuo Codex è ancora vuoto.</p>
                <p className="text-sm text-muted-foreground mt-1">Supera una Prova del Maestro per ottenere la tua prima carta.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.esami.map((e) => (
                  <div key={e.course_id} data-testid={`libretto-row-${e.course_id}`}
                    className="relative bg-card border border-border rounded-md p-5 overflow-hidden transition-all hover:-translate-y-1 hover:shadow-[0_10px_30px_-14px_rgba(114,47,55,0.4)]">
                    <div className={`absolute top-0 right-0 text-[10px] font-bold px-2.5 py-1 rounded-bl-md ${RARITA_STYLE[e.rarita] || "bg-secondary"}`}>
                      {e.rarita}
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">{LIVELLO[e.anno]} · {e.cfu} ⭐{e.percentuale != null ? ` · ${e.percentuale}%` : ""}</p>
                    <h3 className="font-serif text-lg font-bold leading-snug mb-3 pr-14">{e.corso}</h3>
                    <div className="flex items-center gap-2 mb-3">
                      <Trophy className={`h-5 w-5 ${(e.a_regola_arte ?? e.lode) ? "text-accent" : "text-primary"}`} strokeWidth={1.5} />
                      <Badge className={`rounded-full ${(e.a_regola_arte ?? e.lode) ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground"}`}>
                        {e.esito || e.grado}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">Maestro: {e.maestro}</p>
                    <p className="text-xs text-muted-foreground">{fmtDate(e.data)}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="glossario">
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca un termine…"
                  className="pl-9 rounded-sm" data-testid="codex-search" />
              </div>
              <select value={filtro} onChange={(e) => setFiltro(e.target.value)} data-testid="codex-filter"
                className="rounded-sm border border-border bg-card px-3 py-2 text-sm h-10">
                <option value="tutti">Tutti i corsi</option>
                {codex?.corsi.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>

            {!codex ? (
              <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" strokeWidth={1.5} /></div>
            ) : vociFiltrate.length === 0 ? (
              <div className="bg-card border border-border rounded-md p-10 text-center">
                <Library className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" strokeWidth={1.5} />
                <p className="text-muted-foreground">Nessuna voce trovata.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {vociFiltrate.map((v) => (
                  <div key={`${v.termine}-${v.corso_id}`} data-testid={`codex-entry-${v.sbloccata ? "unlocked" : "locked"}`}
                    className={`rounded-md border p-4 ${v.sbloccata ? "bg-card border-border" : "bg-secondary/40 border-dashed border-border"}`}>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <h4 className="font-serif font-bold leading-snug">{v.termine}</h4>
                      {!v.sbloccata && <Lock className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" strokeWidth={1.5} />}
                    </div>
                    {v.sbloccata ? (
                      <p className="text-sm text-muted-foreground leading-relaxed">{v.definizione}</p>
                    ) : (
                      <p className="text-sm text-muted-foreground/70 leading-relaxed blur-[3px] select-none" aria-hidden="true">
                        Definizione bloccata: completa la lectio per svelare questa voce nel tuo Codex.
                      </p>
                    )}
                    <p className="text-[11px] text-muted-foreground mt-2">{v.corso_nome}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
